import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useMemo } from "react";
import { FormProvider, Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  DirectionEnum,
  innerItemEditStyles,
  MAIN_DIRECTIONS,
} from "@ryde/shared";

import {
  ColorIconButton,
  DropdownInputByControl,
  SelectBoundInputByControl,
} from "../../../controls";
import { IStationHubItem } from "./table-station-hub.models";
import { useStationHubItemResolver } from "./use-station-hub-item-validation";

interface IStationHubItemEditProps {
  value: IStationHubItem | undefined;
  existingItems?: IStationHubItem[];
  currentStationId?: number;
  onCancel: () => void;
  onSubmit: (value: IStationHubItem) => void;
}

export const StationHubItemEdit: FC<IStationHubItemEditProps> = ({
  value,
  existingItems = [],
  currentStationId,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { stationHubItemFormValidationSchema } = useStationHubItemResolver(
    existingItems,
    currentStationId,
  );
  const methods = useForm<IStationHubItem>({
    defaultValues: {
      direction: DirectionEnum.Both,
      ...value,
    },
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(
      stationHubItemFormValidationSchema,
    ) as Resolver<IStationHubItem>,
  });

  const {
    trigger,
    getValues,
    control,
    formState: { isValid },
  } = methods;

  const directionSelectItems = useMemo(
    () =>
      MAIN_DIRECTIONS.map(direction => ({
        value: direction,
        label: t(`selectListLabel.Dir${direction}`),
      })),
    [t],
  );

  const onSaveClick = async (): Promise<void> => {
    const isValidated = await trigger();

    if (!isValidated) {
      return;
    }

    const valueSnapshot = getValues();
    onSubmit(valueSnapshot);
  };

  return (
    <FormProvider {...methods}>
      <div className="stationHub-item no-blur-item" style={innerItemEditStyles}>
        <div className="stationHub-item__editors">
          <SelectBoundInputByControl
            name={`hubId`}
            control={control}
            formLabel={t("station.formLabel.hubStationId")}
            formAttributes={{
              dropItemsSource: "station",
            }}
          />

          <SelectBoundInputByControl
            name={`destinationStationId`}
            control={control}
            formLabel={t("station.formLabel.destinationStationId")}
            formAttributes={{
              dropItemsSource: "station",
            }}
          />

          <DropdownInputByControl
            name="direction"
            control={control}
            formLabel={t("general.DIRECTION")}
            formValidation={{
              type: "string",
              rules: [{ validation: "required" }],
            }}
            formAttributes={{
              selectItems: directionSelectItems,
            }}
          />
        </div>
        <div className="stationHub-item__buttons">
          <ColorIconButton
            disabled={!isValid}
            iconType="CHECK"
            edge="end"
            aria-label="save"
            onClick={onSaveClick}
          />

          <ColorIconButton
            iconType="CANCEL"
            edge="end"
            aria-label="cancel"
            onClick={onCancel}
          />
        </div>
      </div>
    </FormProvider>
  );
};
